import React from 'react'
import {useInView} from 'react-intersection-observer'
import styled, {css} from 'styled-components'

import type {InlineVideo} from '@helios/website/src/types/inline-video'

interface Props extends React.VideoHTMLAttributes<HTMLVideoElement> {
    video: InlineVideo
    posterWidth?: number
    posterHeight?: number
    dimensions?: {
        width: number
        height: number
    }
    posterLoading?: 'eager' | 'lazy'
    className?: string
    triggerThreshold?: number
}

export const Video = React.forwardRef<HTMLVideoElement, Props>(
    (
        {
            video,
            poster,
            posterWidth,
            posterHeight,
            posterLoading = 'lazy',
            dimensions,
            className,
            triggerThreshold = 0,
            ...props
        },
        ref,
    ) => {
        const [videoContainerRef, inView] = useInView({
            threshold: triggerThreshold,
            triggerOnce: true,
        })
        let posterSrc = ''
        let aspectRatio: VideoWrapperProps['$aspectRatio'] = 'auto'

        if (poster) {
            const [canonical, defaultParams] = poster.split('?')
            const params = new URLSearchParams(defaultParams)

            if (!canonical.startsWith('data:image') && !canonical.endsWith('.svg')) {
                params.append('dpr', '2')

                if (posterWidth) {
                    params.append('w', posterWidth.toString())
                }

                if (posterHeight) {
                    params.append('h', posterHeight.toString())
                }
            }

            posterSrc = `${canonical}?${params.toString()}`

            // We need the aspect-ratio to act as a placeholder until the video is in view
            if (dimensions?.width && dimensions?.height) {
                aspectRatio = dimensions.width / dimensions.height
            }
        }

        return (
            <VideoWrapper ref={videoContainerRef} $aspectRatio={aspectRatio} className={className}>
                {inView ? (
                    <StyledVideo
                        poster={posterSrc}
                        ref={ref}
                        loop
                        autoPlay
                        playsInline
                        muted
                        disableRemotePlayback
                        {...props}
                    >
                        {video.webm && <source key="webm" src={video.webm} type="video/webm" />}
                        {video.mp4 && <source key="mp4" src={video.mp4} type="video/mp4" />}
                    </StyledVideo>
                ) : (
                    <>
                        {poster && (
                            <StyledPoster
                                src={posterSrc}
                                alt=""
                                loading={posterLoading}
                                width={dimensions?.width}
                                height={dimensions?.height}
                            />
                        )}
                    </>
                )}
            </VideoWrapper>
        )
    },
)

type VideoWrapperProps = {
    $aspectRatio: number | 'auto'
}

const VideoWrapper = styled.div<VideoWrapperProps>`
    position: relative;
    width: 100%;
    aspect-ratio: ${(props) => props.$aspectRatio};
`

const coverStyles = css`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: inherit;
    aspect-ratio: inherit;
`

const StyledPoster = styled.img`
    ${coverStyles};
`

const StyledVideo = styled.video`
    ${coverStyles};

    -webkit-mask-image: -webkit-radial-gradient(white, black);
    -webkit-backface-visibility: hidden;
`
